<template>
	<div class="home">
		<header>
			<h3 class="mobile-headline-5">{{ workflowFinderTitle }}</h3>
			<h5 class="mobile-subtitle-2">
				{{ workflowFinderSubTitle }}
			</h5>
		</header>
		<div class="bullet-points">
			<ul class="body-1">
				<li>
					First, pick your starting material and analyte. Then select the type of analysis you want to apply
					and your application area.
				</li>
				<li>
					Based on selection, we'll show you the products and instruments you need to perform your experiment,
					including the relevant protocols.
				</li>
			</ul>
		</div>
		<div class="categories" v-if="categories.length" ref="categoryList">
			<dropdown-custom
				id="finder-tour-step1"
				:placeholder="categories[1].category"
				:list-options="categories[1].options"
				:value="selections.startingMaterial"
				@input="setSelection('startingMaterial', $event)"
				@dropdown-opened="dropdownOpened"
				key="startingMaterial"
			/>
			<dropdown-custom
				id="finder-tour-step2"
				:placeholder="categories[2].category"
				:list-options="categories[2].options"
				:value="selections.analyte"
				@input="setSelection('analyte', $event)"
				@dropdown-opened="dropdownOpened"
				key="analyte"
			/>
			<dropdown-custom
				id="finder-tour-step3"
				:placeholder="categories[3].category"
				:list-options="categories[3].options"
				:value="selections.technology"
				@input="setSelection('technology', $event)"
				@dropdown-opened="dropdownOpened"
				key="technology"
			/>
			<dropdown-custom
				id="finder-tour-step4"
				:placeholder="categories[0].category"
				:list-options="categories[0].options"
				:value="selections.application"
				@input="setSelection('application', $event)"
				@dropdown-opened="dropdownOpened"
				key="application"
			/>
		</div>
		<div class="workflow-ctas">
			<div class = "show-buttons">
				<md-button v-if="productFinderEnabled"
					id="show-products-btn"
					ref="submit-button"
					class="md-primary md-raised md-button-large contained-btn"
					:disabled="!showProductsEnabled"
					@click="goToProducts"
					>Show Products
				</md-button>
				<md-button
					id="show-workflow-btn"
					ref="submit-button"
					class="md-primary md-raised md-button-large contained-btn"
					:class="{enabled: isValid}"
					:disabled="!isValid"
					@click="goToWorkflow"
					>Show Experiment
				</md-button>
			</div>
			<md-button
				class="md-primary md-button-large reset"
				:disabled="resetDisabled"
				@click="clearSelections"
			>
				<div class="reset-button">
					<base-icon
						:class="{disabled: resetDisabled}"
						icon-name="delete"
						:icon-color="resetDisabled ? '#BCBCBB' : '#1562AD'"
					/>
					<span>Reset</span>
				</div>
			</md-button>
		</div>
	</div>
</template>

<script>
import {mapGetters} from 'vuex';

import {trackEvent, trackObject, createObject} from '@/services/tracking.js';

// mixins
import findSelection from '@/mixins/findSelection';
import DropdownCustom from '@/components/dropdown-custom/DropdownCustom';
import tour from '@/mixins/tour';

export default {
	name: 'Home',
	mixins: [findSelection, tour],
	components: {
		DropdownCustom,
	},
	data() {
		return {
			workflowKeys: null,
			selections: {
				application: null,
				startingMaterial: null,
				analyte: null,
				technology: null,
			},
		};
	},
	props: {
		contextId: {
			default: 'wfc-main-app',
			type: String,
		},
		application: {
			default: '',
			type: String,
		},
		startingMaterial: {
			default: '',
			type: String,
		},
		analyte: {
			default: '',
			type: String,
		},
		technology: {
			default: '',
			type: String,
		},
	},
	watch: {
		contextId(value, oldValue) {
			if (value !== oldValue) {
				this.updateSelections();
			}
		},
	},
	computed: {
		...mapGetters({
			getFinderConfigurationByContextId: 'WorkflowFinder/getFinderConfigurationByContextId',
			getInitialValuesByContextId: 'WorkflowFinder/getInitialValuesByContextId',
		}),
		language() {
			return this.$store.state.LANG;
		},
		categories() {
			const cat = this.$store.state.categories.map((category) => ({
				...category,
				options: category.options
					.map((opt) => {
						if (opt.children.length) {
							const children = opt.children
								.map((child) => ({
									...child,
									disabled: this.optDisabled(child.cvlId, child.key),
								}))
								.sort((a, b) => a.index - b.index);

							const enabledOptions = children.filter((option) => !option.disabled);
							const disabledOptions = children.filter((option) => option.disabled);

							return {
								...opt,
								children: [...enabledOptions, ...disabledOptions],
								disabled: children.every((c) => c.disabled),
							};
						}
						return {...opt, disabled: this.optDisabled(opt.cvlId, opt.key)};
					})
					.sort((a, b) => a.index - b.index),
			}));
			const result = cat.map((category) => {
				const enabledOptions = category.options.filter((option) => !option.disabled);
				const disabledOptions = category.options.filter((option) => option.disabled);
				return {...category, options: [...enabledOptions, ...disabledOptions]};
			});
			return result;
		},
		finderConfiguration() {
			return this.getFinderConfigurationByContextId(this.contextId);
		},
		workflowFinderTitle() {
			return this.finderConfiguration?.title || 'Discover the products and protocols you need';
		},
		workflowFinderSubTitle() {
			return this.finderConfiguration?.subtitle || 'Plan your entire experiment based on research interests.';
		},
		hasAllSelections() {
			return Object.values(this.selections).every((sel) => sel);
		},
		resetDisabled() {
			if (!this.selections) return true;
			return Object.values(this.selections).every((selection) => !selection);
		},
		isValid() {
			return (
				this.selections?.application &&
				this.selections?.startingMaterial &&
				this.selections?.analyte &&
				this.selections?.technology
			);
		},
		showProductsEnabled(){
			return (
				this.selections?.application ||
				this.selections?.startingMaterial ||
				this.selections?.analyte ||
				this.selections?.technology
			);
		},
		productFinderEnabled() {
			return this.$store.state.productFinderEnabled;
		},
		getWorkflowKeys() {
			return Object.keys(this.$store.state.hcube.workflows).map((keys) =>
				keys.split('.').map((key) => this.$store.state.hcube.entryPointsList[key]),
			);
		},
	},
	async created() {
		// set categories
		await this.$store.dispatch('fetchUrls');
		await this.$store.dispatch('fetchHelperFiles');
		await this.$store.dispatch('fetchCategories');
		await this.$store.dispatch('checkPublicationIds');

		const isLoggedIn = this.getLoggedInStatus();
		const s_dtm = createObject('workflow finder', this.getCountry(), isLoggedIn, this.getUserId());
		trackObject(s_dtm);

		this.updateSelections();

		this.workflowKeys = this.getWorkflowKeys;
	},
	mounted() {
		this.resetWFCStoredData();
		if (this.$store.state.noWorkflowFound) {
			this.showHomeBannerInit(this.$store.state.noWorkflowFound);
			this.$store.commit('setNoWorkflowFound', false);
		}
	},
	methods: {
		updateSelections() {
			this.selections.application = this.application ? this.findSelection('Application', this.application) : null;
			this.selections.analyte = this.analyte ? this.findSelection('Analyte', this.analyte) : null;
			this.selections.startingMaterial = this.startingMaterial
				? this.findSelection('Starting material', this.startingMaterial)
				: null;
			this.selections.technology = this.technology ? this.findSelection('Analysis type', this.technology) : null;

			Object.entries(this.selections).forEach(([category, selection]) => {
				if (selection) {
					this.$store.commit('setCategorySelection', {category, selection});
				}
			});

			// updates query in url if theres a selection stored and that query hasn't already been added to $route
			Object.keys(this.selections).forEach((key) => {
				const selection = this.selections[key];
				if (selection && this.$route && !Object.prototype.hasOwnProperty.call(this.$route?.query, key)) {
					this.$emit('selection', {selection, category: key});
				}
			});
			this.startTour('finder')
		},
		async setSelection(category, selection) {
			this.$store.commit('setCategorySelection', {category, selection});

			this.selections[category] = selection;

			this.$emit('selection', {selection, category});

			// enable show-workflow-btn
			if (this.hasAllSelections) {
				await this.$nextTick();

				const button = document.getElementById('show-workflow-btn') || this.$refs['submit-button']?.$el;

				if (button) {
					button.focus();
					this.setNextStep();
				}
				this.$emit('show-workflow-btn');
			}
			// open next category
			else {
				if (this.isTourActive) {
					this.setNextStep()
				} else if (!this.productFinderEnabled) {
					const positionDD = ['startingMaterial', 'analyte', 'technology', 'application'];
					const nextIndexDD = positionDD.findIndex((catName) => !this.selections[catName]);
					this.$refs.categoryList.children[nextIndexDD].__vue__.arrowOpenDD();
				}
			}
		},
		clearSelections() {
			trackEvent('ctaClick', {eVar58: 'Reset | Workflow Configurator'});

			const selection = null;
			['analyte', 'application', 'startingMaterial', 'technology'].forEach((category) => {
				this.selections[category] = selection;
				this.$store.commit('setCategorySelection', {category, selection});
			});
			this.$emit('clear-selection');

			if (window.screen.width <= this.$store.state.mobileView) {
				document.querySelector('header').scrollIntoView({behavior: 'smooth', block: 'start'});
			}
			this.tour.hide();
		},
		dropdownOpened(dropdown) {
			if (this.isTourActive && dropdown.id === this.tour.currentStep.getTarget().id)
				this.tour.currentStep.hide()
		},

		goToWorkflow() {
			const application = this.selections.application.key;
			const startingMaterial = this.selections.startingMaterial.key;
			const analyte = this.selections.analyte.key;
			const technology = this.selections.technology.key;

			/* Navigate to the page for building out the chosen workflow. */
			trackEvent('ctaClick', {eVar58: 'Show workflow | Workflow Configurator'});
			trackEvent('showWorkflow', {
				eVar100: `${application} | ${startingMaterial} | ${analyte} | ${technology}`,
			});
			this.$emit('redirect', {application, startingMaterial, analyte, technology});
			this.resetFinderTourFlag()
		},

		goToProducts() {
			const application = this.selections.application?.key;
			const startingMaterial = this.selections.startingMaterial?.key;
			const analyte = this.selections.analyte?.key;
			const analysisType = this.selections.technology?.key;

			trackEvent('ctaClick', {eVar58: 'Show products | Workflow Configurator'});
			trackEvent('showProducts', {
				eVar100: `${application || undefined} | ${startingMaterial || undefined} | ${analyte || undefined} | ${analysisType || undefined}`,
			});
			window.location.href = `${this.$store.state.productFinderUrl}${window.location.search.replace('technology', 'analysisType')}`;
		},

		isDisabled(cvlId, key) {
			if (!this.getWorkflowKeys || !this.selections) return [];

			// removed extra text on entrypoint group names
			const epName = cvlId.replace(/^WB/, '').replace(/Detail$/, '');

			// get the index of the ep to check against selected ep indexes
			const indexOfCvlId = ['Application', 'StartingMaterial', 'Analyte', 'AnalysisType'].indexOf(epName);
			/**
			 * Creates Array of Objs from selected entry points with
			 * index, name: Entry Point Group, Key,
			 */

			const selectionArr = Object.keys(this.selections)
				.map((selection, i) => {
					const obj = this.selections[selection];
					if (obj)
						return {
							index: i,
							name: obj.cvlId.replace(/^WB/, '').replace(/Detail$/, ''),
							key: obj.key,
						};
				})
				// remove empty values
				.filter((selection) => selection)
				// filter out selection with the same index of the entrypoint we are testing
				// we only want to test against other entrypoint groups */
				.filter((selection) => selection.index !== indexOfCvlId);

			// if there are no selections retun all workflowkeys
			if (!selectionArr?.length) return this.getWorkflowKeys;

			// get array of keys from the selected enterypoints
			const selectionKeys = selectionArr.map((selection) => selection.key);

			const validList = [];
			// create array of valid workflows
			this.getWorkflowKeys.map((wfKeys) => {
				const isValid = selectionKeys.every((selectedKey) => wfKeys.includes(selectedKey));
				if (isValid) validList.push(wfKeys);
			});
			return validList;
		},
		// returns true if options is disabled. Adds 'disabled' to dropdown options
		optDisabled(cvlId, key) {
			return !this.isDisabled(cvlId, key).some((arr) => arr.includes(key));
		},
		// returns true if product is valid ie: visibility for country, isn't phased out... Options wont show in dropdown if false
		resetWFCStoredData() {
			const tourKeys = ['product', 'finder', 'workflow', 'eln'].map(t => `tour-${t}`);
			const tourValues = tourKeys.map(k => [k, localStorage.getItem(k)]);
			localStorage.clear();
			tourValues.forEach(([k, v]) => { if (v !== null) localStorage.setItem(k, v); });
			localStorage.setItem(
				'registrationOriginApplication',
				JSON.stringify({value: 'WFC', timestamp: Date.now()}),
			);
			this.$store.commit('setWorkflowHeadline', null);
			this.$store.commit('setWorkflowDefaultHeadline', null);
			this.$store.commit('setWorkFlowId', null);
			this.$store.commit('setMyWorkflow', null);
			this.$store.commit('setMyWorkflows', null);
			this.$store.commit('setLastModDate', null);
		},
		getLoggedInStatus() {
			const jwt = this.$store.state.jwt;
			if (jwt) return `${jwt.lastName}, ${jwt.firstName}`;
			return null;
		},
		getCountry() {
			return this.$store.state.jwt ? this.$store.state.jwt.country : this.$store.state.COUNTRY;
		},
		getUserId() {
			const jwt = this.$store.state.jwt;
			if (jwt) return jwt.sub;
			return 'not loggedin';
		},
	},
};
</script>

<style lang="scss" scoped>
.home {
	background-color: $white;
	margin-top: 40px;
	padding: 2.18rem 2.4rem;
	@media (max-width: $mobile) {
		margin-top: 18px;
		padding: 16px;
	}

	header {
		padding-bottom: 2rem;

		h3,
		h5 {
			margin: 0;
		}

		h5 {
			padding-top: 0.7rem;
		}

		@media (max-width: $mobile) {
			padding-bottom: 1rem;
			h5 {
				padding-top: 1rem;
			}
		}
	}

	.categories {
		@media (max-width: $mobile) {
			div:not(div:last-of-type) {
				margin-bottom: 33px;
			}
		}
		@media (min-width: $mobile + 1) {
			@include flexRow;
			justify-content: space-between;
			div {
				width: 100%;
				margin-right: 16px;
				flex: 1;
				min-width: 0;

				&:nth-child(4) {
					margin-right: 0px;
				}
			}
		}
	}

	.workflow-ctas {
		.show-buttons {
				display: flex;
				gap: 16px;
			}
		padding-top: 39px;
		@media (min-width: $mobile + 1) {
			@include flexRow;
			padding-top: 1.9rem;
			justify-content: space-between;
			flex-flow: row-reverse;
		}
		@media (max-width: $mobile) {
			.show-buttons {
				display: flex;
				flex-direction: column;
			}
			#show-workflow-btn, #show-products-btn {
				display: block;
				margin: 0 auto 24px;
				width: 100%;
			}
		}

		.reset-button {
			display: flex;
			align-items: center;

			span {
				height: 24px;
				line-height: 26px;
				text-align: center;
			}
		}
	}
}

.bullet-points {
	padding-bottom: 2rem;
	ul {
		padding-inline-start: 32px;
	}
	@media (max-width: $mobile) {
		padding-bottom: 1rem;
		ul {
			padding-inline-start: 24px;
		}
	}
}
</style>
